import type { TrackAssessmentCategory } from '@backrs_ui/common-ui';

import type { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';

export const SUCCESS_SYSTEM_SECTION_IDS = {
  overview: 'success-system-section-overview',
  lifeAspirations: 'success-system-section-life-aspirations',
  lifeBlueprint: 'success-system-section-life-blueprint',
  fundamentals: 'success-system-section-fundamentals',
  fourUnlocks: 'success-system-section-four-unlocks',
  knowYourStrengths: 'success-system-section-know-your-strengths',
  bigDecisions: 'success-system-section-big-decisions',
  careerTracks: 'success-system-section-career-tracks',
  educationInvestments: 'success-system-section-education-investments',
  careerPaths: 'success-system-section-career-paths',
  trackProgression: 'success-system-section-track-progression',
  investWhereItCounts: 'success-system-section-invest-where-it-counts',
  calendarOfMilestones: 'success-system-section-calendar-of-milestones',
} as const;

export type SuccessSystemSectionId =
  (typeof SUCCESS_SYSTEM_SECTION_IDS)[keyof typeof SUCCESS_SYSTEM_SECTION_IDS];

export type SuccessSystemTopic = Readonly<{
  id: SuccessSystemSectionId;
  title: string;
  body: string;
  whyItMatters: string;
  toolId?: StickerCardId;
}>;

export type SuccessSystemGroup = Readonly<{
  id: SuccessSystemSectionId;
  title: string;
  subtitle: string;
  lubav?: SuccessSystemLubav;
  infoLabel?: string;
}>;

export type SuccessSystemLubav = Readonly<{ label: string; talkingPoints: string }>;

export const successSystemContent = {
  bannerAlt: 'A goal without a system is just a wish. — Antoine de Saint-Exupéry',
  overview: {
    title: 'The Backrs Success System',
    lubav: {
      label: 'What makes this system different?',
      talkingPoints: `**Track-Specific, Not One-Size-Fits-All**
It recognizes that success looks different for an electrician, a teacher, a software engineer, or a corporate attorney. Each track has its own bar, and the system reverse-engineers what it takes to meet it.

**Based on Real Outcomes, Not Opinions**
The Markers of Success aren't based on theory or aspiration—they're drawn from actual data on what successful adults in each field did in high school, college, and early career.

**Holistic but Grounded**
It doesn't just include GPA or test scores. It sets expectations across four dimensions that actually drive long-term success: academics, experiences, mindsets/skills, and networks.

**Grade-by-Grade, Not Just End Goals**
It breaks down the journey into specific, measurable markers starting as early as 8th grade—what to do this year, not just someday.`,
    },
    diagramHint: 'Select any part of the system to learn more',
    description: [
      'The Backrs Success System is a holistic system of progression that defines what young people must build to succeed in school, career, and life. We built it backwards from how real people have succeeded in the real world — then strengthened it with insights from other proven progression systems like martial arts, scouting, the military, and leading apprenticeship programs.',
      'It integrates academics, achievement, mindsets, skills, and networks — all aligned with how the economy and careers are evolving in an AI-driven world. Unlike generic advice, it gives students and families a clear framework for what matters most, when it matters, and how to keep growing.',
    ],
  },
  lifeAspirations: {
    group: {
      id: SUCCESS_SYSTEM_SECTION_IDS.lifeAspirations,
      title: 'Life Aspirations',
      subtitle: 'Defining the life you want to build',
      lubav: {
        label: 'Why do I need the Success System?',
        talkingPoints: `1. **Make big decisions with confidence**
College major, career path, what to focus on—get clarity on choices that will shape your future.

2. **Know what you actually need to be doing**
Stop guessing. See exactly what it takes to reach your goals and whether you're doing it.

3. **Understand how all the pieces fit together**
See how your classes, experiences, skills, and relationships connect to create real career success.

4. **Get the real, no-BS keys to success**
Skip the generic advice. Learn what actually separates people who make it from those who don't.`,
      },
    },
    lifeBlueprint: {
      id: SUCCESS_SYSTEM_SECTION_IDS.lifeBlueprint,
      title: 'Build a Life Blueprint',
      body: "Start by capturing the big-picture vision of the life a young adult wants to build — including lifestyle goals, family priorities, career flexibility, sources of meaning, and the activities that energize or drain them. It's about understanding not just what you want to do for work, but what kind of life you want to live.",
      whyItMatters:
        "Career and education choices only make sense in the context of the life you're aiming for. By clarifying life aspirations early, young adults can align their career track, educational investments, and daily choices with a bigger purpose — ensuring that the path they work so hard to achieve actually leads to a life that feels meaningful, sustainable, and energizing.",
      toolId: 'blueprint',
    },
  },
  fundamentals: {
    group: {
      id: SUCCESS_SYSTEM_SECTION_IDS.fundamentals,
      title: 'Fundamentals',
      subtitle: 'Defining the life a student wants to build',
      infoLabel: 'Click any unlock to learn more',
    },
    fourUnlocks: {
      id: SUCCESS_SYSTEM_SECTION_IDS.fourUnlocks,
      title: 'Four Unlocks to Success',
      body: "These unlocks apply in every career, at every level. They're the difference between simply graduating and thriving — building resilience, credibility, and networks that compound over time.",
      aside: 'The timeless, research-backed building blocks of long-term success.',
      unlocks: [
        {
          id: 1,
          slug: 'academic-foundations',
          name: 'Academic Foundations',
          shortname: 'Academics',
          description:
            'Learn faster, think sharper, and build the skills that every job—and every opportunity—demands.',
        },
        {
          id: 2,
          slug: 'real-world-experience',
          name: 'Real World Experience & Achievement',
          shortname: 'Achievement',
          description:
            'Prove you can deliver results by doing hard, valuable things that exist outside the classroom.',
        },
        {
          id: 3,
          slug: 'community-of-success',
          name: 'Engagement in Communities',
          shortname: 'Communities',
          description:
            'The people around you shape your future—so build networks that challenge, support, and open doors.',
        },
        {
          id: 4,
          slug: 'mindsets-and-skills',
          name: 'Mindsets & Skills for a Post-AI Economy',
          shortname: 'Skills',
          description:
            'Work habits, grit, and adaptability that keep you moving forward—no matter how the world changes.',
        },
      ] satisfies readonly TrackAssessmentCategory[],
    },
    knowYourStrengths: {
      id: SUCCESS_SYSTEM_SECTION_IDS.knowYourStrengths,
      title: 'Know Your Strengths',
      body: "A process for identifying your natural talents, skills, and energizers — the things you do well and enjoy doing — along with the areas that drain your energy or require more effort. It's about building self-awareness so students can align choices with who they are.",
      whyItMatters:
        "Success comes faster when young adults play to their strengths. Knowing what you're good at (and what you're not) helps you choose the right track, majors, and opportunities — and makes it easier to stand out. It also prevents wasted time chasing paths that don't fit, giving students confidence and clarity in their progression.",
      toolId: 'life_readiness',
    },
  },
  bigDecisions: {
    group: {
      id: SUCCESS_SYSTEM_SECTION_IDS.bigDecisions,
      title: 'Big Decisions',
      subtitle: 'These are the high-impact decisions that shape your opportunities.',
    },
    topics: [
      {
        id: SUCCESS_SYSTEM_SECTION_IDS.careerTracks,
        title: 'Career Tracks',
        body: 'The level of selectivity and competitiveness you operate in — from trades/technical to top-tier professional. Tracks set the standards for GPA, rigor, and experiences that are "enough" to stay in the game.',
        whyItMatters:
          'Students may change paths often, but their track defines their ceiling. Moving down a track is easy; moving up is rare. Choosing and committing to a target track early allows young adults to plan strategically and avoid doors closing later.',
        toolId: 'tracks_to_succeed',
      },
      {
        id: SUCCESS_SYSTEM_SECTION_IDS.educationInvestments,
        title: 'Education Investments',
        body: 'The major decisions that shape your trajectory: which pathway (trade, college, or work), which school, which college, and which major. Research shows GPA, rigor of major, and college selectivity all interact to influence outcomes.',
        whyItMatters:
          'Education is the single largest investment most families make. The right choices amplify opportunities and return on investment; the wrong ones can lead to underemployment, debt, or limited mobility. Students need clear guidance on which educational paths align with their target track.',
        toolId: 'explore_universities_sticker',
      },
      {
        id: SUCCESS_SYSTEM_SECTION_IDS.careerPaths,
        title: 'Career Paths',
        body: 'The specific fields or professions within which you work (e.g., business, healthcare, technology, education, arts). Paths represent the roles, tasks, and skills you pursue in your day-to-day career.',
        whyItMatters:
          'Most people change paths two or three times in their life. Understanding this flexibility prevents students from feeling "locked in" and expands their imagination about what\'s possible. The key is to stay on track while exploring different paths.',
        toolId: 'jobs_paths_for_you',
      },
    ] satisfies readonly SuccessSystemTopic[],
  },
  trackProgression: {
    group: {
      id: SUCCESS_SYSTEM_SECTION_IDS.trackProgression,
      title: 'Track Progression',
      subtitle: 'Defining the life you want to build',
      lubav: {
        label: 'How did Backrs develop this system?',
        talkingPoints: `**Research-Driven Foundation**
We analyzed 82 different career paths to understand what successful professionals actually did during their formative years. This wasn't based on theory or wishful thinking—it was grounded in real outcomes and measurable achievements.

**Learning from Proven Systems**
We studied progression systems that have worked for centuries—martial arts belt systems, military advancement, scouting programs, and leading apprenticeships. These systems share common principles about how people develop mastery over time.

**Future-Focused Design**
The system integrates how careers are evolving in an AI-driven world. It emphasizes the skills, mindsets, and capabilities that will remain valuable as technology advances—things like adaptability, creative problem-solving, and relationship-building.

**Holistic Integration**
Rather than focusing solely on academics or test scores, we created a framework that balances academic foundations, real-world experience, essential mindsets and skills, and community engagement—because success requires all four working together.`,
      },
    },
    investWhereItCounts: {
      id: SUCCESS_SYSTEM_SECTION_IDS.investWhereItCounts,
      title: 'Invest where it counts',
      body: 'The level of selectivity and competitiveness you operate in — from trades/technical to top-tier professional. Tracks set the standards for GPA, rigor, and experiences that are "enough" to stay in the game.',
      whyItMatters:
        'Students may change paths often, but their track defines their ceiling. Moving down a track is easy; moving up is rare. Choosing and committing to a target track early allows young adults to plan strategically and avoid doors closing later.',
      toolId: 'are_you_on_track',
    },
    calendarOfMilestones: {
      id: SUCCESS_SYSTEM_SECTION_IDS.calendarOfMilestones,
      title: 'Calendar of Milestones',
      body: 'A month-by-month roadmap from 8th grade through college graduation, spelling out when to focus on academics, experiences, applications, and skill-building.',
      whyItMatters:
        "Timing is everything. Missing key milestones (like course choices, test prep, internships, or applications) can quietly derail a student's trajectory. An integrated calendar ensures students and families know not just what matters, but when.",
    },
  },
} as const;
